import { useCallback, useEffect, useState } from 'react';

// Hooks/Utils
import useFetchIntervalData from '../../../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../../../packages/hooks/useSubscriptionClick';

// Utils
import { getMatchByPlayerId } from '../../../../../packages/services/football/matches';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { normalizeRoundMatches } from '../../normalizers/round/normalizeRoundMatchesResponseData';

// Types
import { IPlayerConfig, SDKOptions, INormalizedMatchV2 } from '../../../../../packages/types';
import { IGeneralSubscriberInfo } from '../../../../../packages/constants/subscribeTypes';

interface IUseMatchArgs extends IGeneralSubscriberInfo {
  dataEntityLinks?: IPlayerConfig['dataEntityLinks'];
  dataTeamNameType?: IPlayerConfig['dataTeamNameType'];
  dataRefreshTime?: IPlayerConfig['dataRefreshTime'];
  dataPlayer?: IPlayerConfig['dataPlayer'];
  dataMatchId?: IPlayerConfig['dataMatchId'];
  sdkOptions: SDKOptions;
  dataOddsDisplay?: IPlayerConfig['dataOddsDisplay'];
  dataOddsMarketValueType?: IPlayerConfig['dataOddsMarketValueType'];
  dataOddsBettingId?: IPlayerConfig['dataOddsBettingId'];
  dataOddsMarket?: IPlayerConfig['dataOddsMarket'];
  dataOddsPreEventOnly?: IPlayerConfig['dataOddsPreEventOnly'];
}

export default function useMatchByPlayerId({
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  subscribers,
  dataWidgetCuid,
  dataPlayer,
  dataMatchId,
  dataEntityLinks,
  dataTeamNameType,
  sdkOptions,
  dataOddsDisplay,
  dataOddsBettingId,
  dataOddsMarket,
  dataOddsMarketValueType,
  dataOddsPreEventOnly,
}: IUseMatchArgs) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [match, setMatch] = useState<INormalizedMatchV2>();

  const params = {
    player_id: dataPlayer,
    dataOddsDisplay: dataOddsDisplay,
    dataOddsBettingId: dataOddsBettingId,
    dataOddsMarket: dataOddsMarket,
    dataOddsMarketValueType: dataOddsMarketValueType,
    dataOddsPreEventOnly: dataOddsPreEventOnly,
  };

  const fetchMatch = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      if (dataPlayer && !dataMatchId) {
        manageLoader && setIsLoading(true);
        getMatchByPlayerId({ params, sdkOptions })
          .then(({ matches }) => {
            manageLoader && setIsLoading(false);
            const normalizedMatches = normalizeRoundMatches({ matches, dataEntityLinks, dataTeamNameType, sdkOptions });
            normalizedMatches && normalizedMatches.length > 0 && setMatch(normalizedMatches[0]);
            !isFromInterval && stackOdds(matches, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [dataPlayer, dataMatchId, dataTeamNameType],
  );

  useFetchIntervalData(fetchMatch, dataRefreshTime);

  useEffect(() => {
    fetchMatch(true);
  }, [fetchMatch]);

  return {
    widgetData: { match, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
}
